<template>
  <div>
    <b-row class="align-items-center mb-1 no-print">
      <b-col>
        <h1 class="font-weight-bolder">
          Cartera (Receivables)
        </h1>
      </b-col>

      <b-col
        cols="auto"
        class="no-print"
      >
        <div class="d-flex align-items-center flex-wrap justify-content-end">
          <div
            class="d-flex align-items-center mr-2"
            style="flex:0 0 auto;"
          >
            <b-input-group size="sm">
              <b-form-input
                v-model.trim="searchText"
                placeholder="Buscar folio o factura"
                :disabled="loading"
                style="width: 220px;"
                @keyup.enter="applySearch"
              />
              <b-input-group-append>
                <b-button
                  variant="primary"
                  :disabled="loading"
                  @click="applySearch"
                >
                  Buscar
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </div>

          <b-button
            class="mr-2"
            variant="success"
            :disabled="loading"
            @click="onExportCsv"
          >
            Exportar CSV
          </b-button>

          <b-button
            variant="outline-secondary"
            :disabled="loading"
            @click="onPrint"
          >
            Imprimir
          </b-button>
        </div>
      </b-col>

    </b-row>

    <!-- Filtros (NO imprimir) -->
    <b-card class="mb-1 no-print">
      <b-row>
        <b-col
          v-if="!isCustomerUser"
          md="4"
        >
          <b-form-group label="Cliente">
            <b-form-select
              v-model="filters.customer_id"
              :options="customerOptions"
              :disabled="loading"
              @change="onCustomerChange"
            />
          </b-form-group>
        </b-col>

        <b-col :md="isCustomerUser ? 6 : 4">
          <b-form-group label="Proyecto">
            <b-form-select
              v-model="filters.project_id"
              :options="projectOptions"
              :disabled="loading || !filters.customer_id"
              @change="refetch"
            />
          </b-form-group>
        </b-col>

        <b-col
          :md="isCustomerUser ? 6 : 4"
          class="d-flex align-items-center"
        >
          <b-form-checkbox
            v-model="filters.unpaid_only"
            switch
            class="mr-2"
            :disabled="filters.pending_complement_only"
            @change="refetch"
          >
            Sólo no pagadas
          </b-form-checkbox>
          <b-form-checkbox
            v-model="filters.only_overdue"
            switch
            class="mr-2"
            :disabled="filters.pending_complement_only"
            @change="refetch"
          >
            Sólo vencidas
          </b-form-checkbox>
          <b-form-checkbox
            v-if="!isCustomerUser"
            v-model="filters.pending_complement_only"
            switch
            @change="refetch"
          >
            Sólo pendientes de REP
          </b-form-checkbox>
        </b-col>
      </b-row>

      <b-alert
        v-if="filters.pending_complement_only"
        show
        variant="warning"
        class="mb-0 mt-1 yc-fiscal-mode-alert"
      >
        <strong>Vista fiscal interna.</strong>
        Muestra facturas PPD que ya se cobraron pero siguen sin complemento de pago.
        No es la cartera del cliente y no coincide con su estado de cuenta.
      </b-alert>
    </b-card>

    <!-- Header de crédito (pantalla) -->
    <b-card
      v-if="filters.customer_id && creditHeader"
      class="mb-1 no-print yc-credit-card"
      :class="{ 'is-over-limit': isOverLimit }"
    >
      <div class="yc-credit-header">
        <div class="yc-client-block">
          <div class="yc-eyebrow">
            Cliente
          </div>
          <div class="yc-client-title-row">
            <h4 class="yc-client-title">
              {{ creditHeader.name }}
            </h4>
            <b-badge
              :variant="creditHeader.credit_enabled ? 'success' : 'secondary'"
              class="yc-status-badge"
            >
              {{ creditHeader.credit_status }}
            </b-badge>
          </div>
          <div class="yc-client-meta">
            <span>Plazo: {{ creditHeader.credit_term_days }} días</span>
            <span v-if="creditHeader.contact_name">Contacto: {{ creditHeader.contact_name }}</span>
          </div>
        </div>

        <div class="yc-credit-limits">
          <div class="yc-limit-item">
            <span>Límite</span>
            <strong>$ {{ money(creditHeader.credit_limit) }}</strong>
          </div>
          <div
            class="yc-limit-item"
            :class="{ 'is-negative': isOverLimit }"
          >
            <span>Disponible</span>
            <strong>$ {{ money(creditHeader.disponible) }}</strong>
          </div>
        </div>
      </div>
      <div
        v-if="isOverLimit"
        class="yc-credit-alert"
      >
        El cliente excede su línea de crédito por $ {{ money(creditHeader.over_limit_amount) }}.
      </div>
      <div
        v-if="canIssueRep"
        class="yc-rep-panel"
      >
        <div class="yc-rep-info">
          <div class="yc-rep-title-row">
            <div>
              <div class="yc-eyebrow">
                Complemento de pago
              </div>
              <h5 class="yc-rep-title">
                Generar REP
              </h5>
            </div>
            <b-button
              v-if="selectedPendingRepId"
              size="sm"
              variant="outline-primary"
              class="yc-rep-new-payment"
              :disabled="loading || generatingRep"
              @click="clearPendingRepSelection"
            >
              Nuevo pago
            </b-button>
          </div>

          <div class="yc-rep-flow">
            <div class="yc-rep-step">
              <span>1</span>
              Guarda el monto recibido
            </div>
            <div class="yc-rep-step">
              <span>2</span>
              Selecciona un pago pendiente
            </div>
            <div class="yc-rep-step">
              <span>3</span>
              Relaciona facturas PPD, ajusta el monto de cada una y emite
            </div>
          </div>

          <div class="yc-rep-progress">
            <div class="yc-rep-progress-labels">
              <span>Asignado $ {{ money(selectedRepTotal) }}</span>
              <span>Pago $ {{ money(effectiveRepAmount || 0) }}</span>
            </div>
            <b-progress
              height="8px"
              :max="100"
              :value="repAssignmentProgress"
              :variant="repProgressVariant"
            />
          </div>

          <div class="yc-rep-summary">
            <span>Pago seleccionado: {{ selectedPendingRepLabel }}</span>
            <span>Facturas: {{ selectedRepRows.length }}</span>
            <span>Disponible guardado: $ {{ money(selectedRepAvailableBalance) }}</span>
            <span v-if="repAmountMessage">{{ repAmountMessage }}</span>
          </div>
        </div>
        <div class="yc-rep-actions">
          <b-form-select
            v-model="selectedRepTaxInformationId"
            size="sm"
            :options="repTaxInformationOptions"
            :disabled="loading || generatingRep"
            class="yc-rep-tax-select"
          />
          <currency-input
            v-model="repAmount"
            size="sm"
            min="0"
            :precision="2"
            :disabled="loading || generatingRep || Boolean(selectedPendingRepId)"
            class="yc-rep-input"
            placeholder="Monto recibido"
          />
          <b-form-input
            v-model="repPaymentDate"
            type="date"
            size="sm"
            :disabled="loading || generatingRep"
            class="yc-rep-date-input"
          />
          <b-button
            size="sm"
            variant="success"
            :disabled="!canSubmitRep"
            @click="onIssueRep"
          >
            {{ generatingRep ? 'Generando...' : 'Generar REP' }}
          </b-button>
          <b-button
            size="sm"
            variant="outline-primary"
            :disabled="!canSaveRepReceipt"
            @click="onSaveRepReceipt"
          >
            {{ saveRepReceiptLabel }}
          </b-button>
        </div>
      </div>
      <div class="yc-aging-summary">
        <div
          v-for="metric in creditSummaryMetrics"
          :key="metric.key"
          class="yc-aging-metric"
          :class="metric.class"
        >
          <small>{{ metric.label }}</small>
          <strong>$ {{ money(metric.value) }}</strong>
        </div>
      </div>
    </b-card>

    <!-- Encabezado para IMPRESIÓN -->
    <div
      v-if="filters.customer_id && creditHeader"
      class="print-only mb-2"
    >
      <div class="d-flex justify-content-between align-items-start yc-print-header">
        <div class="d-flex align-items-center">
          <img
            src="/logo.png"
            alt="Logo"
            class="yc-print-logo"
          >
          <div>
            <div class="font-weight-bolder yc-company">
              PLATAFORMA DIGITAL YOC SAPI DE CV
            </div>
            <div class="yc-title">
              Estado de Cuenta Con Saldos Pendientes
            </div>
          </div>
        </div>
        <div class="text-right small">
          <div>Fecha: {{ printDate }}</div>
        </div>
      </div>

      <!-- Línea de datos (sin CLIENTE, tipo fijo CHECKPLUS) -->
      <div class="mt-2 small yc-print-grid-4">
        <div><strong>NOMBRE</strong>: {{ creditHeader ? creditHeader.name : '—' }}</div>
        <div><strong>TIPO DE CRÉDITO</strong>: CHECKPLUS</div>
        <div><strong>PLAZO</strong>: {{ creditHeader ? creditHeader.credit_term_days : '—' }} DÍAS</div>
        <div><strong>LÍMITE DE CRÉDITO</strong>: $ {{ money(creditHeader ? creditHeader.credit_limit : 0) }}</div>
      </div>

      <!-- Resumen con buckets -->
      <div class="mt-2 small yc-print-grid-6">
        <div><strong>TOTAL</strong>: <span class="yc-money">$ {{ money(creditHeader && creditHeader.summary ? creditHeader.summary.total : 0) }}</span></div>
        <div><strong>AL CORRIENTE</strong>: <span class="yc-money">$ {{ money(creditHeader && creditHeader.summary ? creditHeader.summary.al_corriente : 0) }}</span></div>
        <div><strong>1 A 15</strong>: <span class="yc-money">$ {{ money(creditHeader && creditHeader.summary ? creditHeader.summary.a_1_15 : 0) }}</span></div>
        <div><strong>16 A 30</strong>: <span class="yc-money">$ {{ money(creditHeader && creditHeader.summary ? creditHeader.summary.a_16_30 : 0) }}</span></div>
        <div><strong>+30</strong>: <span class="yc-money">$ {{ money(creditHeader && creditHeader.summary ? creditHeader.summary.a_30_mas : 0) }}</span></div>
        <div><strong>VENCIDO</strong>: <span class="yc-money">$ {{ money(creditHeader && creditHeader.summary ? creditHeader.summary.vencido : 0) }}</span></div>
      </div>
    </div>

    <!-- Tabla -->
    <b-overlay :show="loading">
      <receivables-table
        ref="receivablesTable"
        :rows="rows"
        :selected-tax-information-id="selectedRepTaxInformationId"
        :reserved-rep-quote-ids="reservedRepQuoteIds"
        :allowed-reserved-rep-quote-ids="allowedReservedRepQuoteIds"
        :rep-unassigned-amount="repUnassignedAmount"
        :rep-payment-amount="Number(effectiveRepAmount || 0)"
        @mark-paid="onMarkPaid"
        @pay-partial="onPayPartial"
        @unmark-paid="onUnmarkPaid"
        @save-invoice="onSaveInvoice"
        @rep-selection-change="onRepSelectionChange"
      />

      <div
        v-if="canIssueRep && repHistory.length"
        class="yc-rep-history no-print"
      >
        <div class="yc-rep-history-header">
          <div>
            <h5>Historial de REPs</h5>
            <small>
              {{ repHistory.length }} registros · {{ pendingRepCount }} pendientes · {{ emittedRepCount }} emitidos
            </small>
          </div>
          <div class="yc-rep-history-tools">
            <b-form-input
              v-model.trim="repHistorySearch"
              size="sm"
              placeholder="Buscar REP, UUID o folio"
              class="yc-rep-history-search"
            />
            <b-form-select
              v-model="repHistoryStatusFilter"
              size="sm"
              :options="repHistoryStatusOptions"
              class="yc-rep-history-filter"
            />
          </div>
        </div>
        <b-table
          :items="paginatedRepHistory"
          :fields="repHistoryFields"
          small
          responsive
          show-empty
        >
          <template #empty>
            <div class="text-center text-muted py-1">
              No hay REPs con esos filtros.
            </div>
          </template>
          <template #cell(created_at)="d">
            {{ formatDateTime(d.item.created_at) }}
          </template>
          <template #cell(payment_date)="d">
            {{ formatDate(d.item.payment_date) }}
          </template>
          <template #cell(rep_select)="d">
            <b-form-radio
              v-model="selectedPendingRepId"
              name="pending-rep-selection"
              :value="d.item.id"
              :disabled="!isPendingRepSelectable(d.item)"
              @change="onPendingRepSelected(d.item)"
            />
          </template>
          <template #cell(amount)="d">$ {{ money(d.item.amount) }}</template>
          <template #cell(applied_amount)="d">$ {{ money(d.item.applied_amount) }}</template>
          <template #cell(facturapi_payment_invoice_reference)="d">
            {{ d.item.facturapi_payment_invoice_reference || '—' }}
          </template>
          <template #cell(status)="d">
            {{ repHistoryStatusLabel(d.item) }}
          </template>
          <template #cell(tax_information)="d">
            {{ d.item.tax_information && d.item.tax_information.corporate_name }} · {{ d.item.tax_information && d.item.tax_information.rfc }}
          </template>
          <template #cell(allocations)="d">
            <span>{{ repHistoryQuoteSummary(d.item) }}</span>
            <small
              v-if="isPendingAllocationRep(d.item) && Number(d.item.allocation_difference || 0) > 0"
              class="d-block text-warning"
            >
              Faltan facturas por $ {{ money(d.item.allocation_difference) }}
            </small>
          </template>
          <template #cell(actions)="d">
            <b-button
              v-if="canVoidPendingRep(d.item)"
              size="sm"
              variant="outline-danger"
              :disabled="voidingPendingRep"
              @click="openVoidPendingRepModal(d.item)"
            >
              Anular
            </b-button>
            <b-button
              v-if="canSyncRepCancellation(d.item)"
              size="sm"
              variant="outline-warning"
              class="ml-50"
              :disabled="syncingRepCancellation"
              @click="syncRepCancellation(d.item)"
            >
              Actualizar
            </b-button>
            <b-button
              v-if="canCancelRep(d.item)"
              size="sm"
              variant="outline-danger"
              class="ml-50"
              :disabled="cancelingRep"
              @click="openCancelRepModal(d.item)"
            >
              Cancelar
            </b-button>
          </template>
        </b-table>
        <div
          v-if="filteredRepHistory.length > repHistoryPerPage"
          class="yc-rep-history-footer"
        >
          <small>
            Mostrando {{ paginatedRepHistory.length }} de {{ filteredRepHistory.length }} registros filtrados.
          </small>
          <b-pagination
            v-model="repHistoryPage"
            :total-rows="filteredRepHistory.length"
            :per-page="repHistoryPerPage"
            size="sm"
            align="right"
            class="mb-0"
          />
        </div>
      </div>
    </b-overlay>

    <!-- Leyenda pie de página solo impresión -->
    <div class="print-only yc-print-note">
      Estimado cliente le recordamos que su cuenta está afiliada al Buró de Crédito, por lo cual es importante mantener su cuenta al corriente para no afectar su historial crediticio.
    </div>

    <!-- Sin datos -->
    <b-alert
      v-if="!loading && rows.length === 0"
      show
      variant="light"
      class="mt-1 no-print"
    >
      No hay registros 🧾
    </b-alert>
    <b-modal
      id="cancelReceivableRep"
      title="Cancelar REP"
      ok-title="Cancelar REP"
      cancel-title="Cerrar"
      ok-variant="danger"
      :ok-disabled="cancelingRep || !canSubmitRepCancellation"
      @ok.prevent="cancelSelectedRep"
      @hidden="resetRepCancellation"
    >
      <b-alert
        show
        variant="warning"
      >
        Si Facturapi confirma la cancelación, se reversan los montos aplicados a las facturas relacionadas y el intento queda auditado.
      </b-alert>
      <div
        v-if="repToCancel"
        class="mb-1"
      >
        <strong>{{ repToCancel.facturapi_payment_invoice_reference || 'REP' }}</strong>
        <span class="text-muted">· $ {{ money(repToCancel.amount) }}</span>
        <div class="small text-muted">
          {{ repHistoryQuoteSummary(repToCancel) }}
        </div>
      </div>
      <b-form-group label="Motivo SAT">
        <b-form-select
          v-model="repCancellationForm.motive"
          :options="cancellationMotiveOptions"
        />
      </b-form-group>
      <b-form-group
        v-if="repCancellationForm.motive === '01'"
        label="UUID sustituto"
      >
        <b-form-input
          v-model.trim="repCancellationForm.substitution"
          placeholder="UUID del CFDI que sustituye"
        />
      </b-form-group>
      <b-form-group label="Razón operativa">
        <b-form-textarea
          v-model.trim="repCancellationForm.reason"
          rows="3"
          max-rows="5"
          placeholder="Describe por qué se cancela el REP"
        />
      </b-form-group>
    </b-modal>
    <b-modal
      id="voidPendingReceivablePayment"
      title="Anular pago guardado"
      ok-title="Anular pago"
      cancel-title="Cerrar"
      ok-variant="danger"
      :ok-disabled="voidingPendingRep || !canSubmitVoidPendingRep"
      @ok.prevent="voidSelectedPendingRep"
      @hidden="resetVoidPendingRep"
    >
      <b-alert
        show
        variant="warning"
      >
        El pago guardado dejará de reservar facturas y no se usará para emitir REP. El registro queda en historial con la razón de anulación.
      </b-alert>
      <div
        v-if="pendingRepToVoid"
        class="mb-1"
      >
        <strong>$ {{ money(pendingRepToVoid.remaining_amount) }}</strong>
        <span class="text-muted">· {{ repHistoryQuoteSummary(pendingRepToVoid) }}</span>
      </div>
      <b-form-group label="Razón de anulación">
        <b-form-textarea
          v-model.trim="voidPendingRepForm.reason"
          rows="3"
          max-rows="5"
          placeholder="Describe por qué se anula este pago guardado"
        />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ReceivablesTable from '@/views/finance/ReceivablesTable.vue'
import CurrencyInput from '@/components/CurrencyInput.vue'

export default {
  components: { ReceivablesTable, CurrencyInput },

  data() {
    return {
      loading: false,
      searchText: '',
      repAmount: null,
      repPaymentDate: '',
      selectedRepTaxInformationId: '',
      selectedRepRows: [],
      selectedPendingRepId: '',
      repHistory: [],
      repHistorySearch: '',
      repHistoryStatusFilter: 'available',
      repHistoryPage: 1,
      repHistoryPerPage: 8,
      generatingRep: false,
      cancelingRep: false,
      syncingRepCancellation: false,
      voidingPendingRep: false,
      repToCancel: null,
      pendingRepToVoid: null,
      repCancellationForm: {
        motive: '02',
        substitution: '',
        reason: '',
      },
      voidPendingRepForm: {
        reason: '',
      },
      printDate: new Date().toLocaleDateString('es-MX'),
      // Guardas anti-bucle/raza para sync con la ruta
      suppressRouteSync: false,
      filters: {
        customer_id: '',
        project_id: '',
        unpaid_only: true,
        only_overdue: false,
        pending_complement_only: false,
        q: '',
      },
    }
  },

  computed: {
    ...mapGetters('auth', ['currentUser']),
    ...mapGetters('finance', [
      'receivables', 'receivablesSummary', 'receivablesCustomers',
      'receivablesProjects', 'receivablesCreditHeader',
    ]),
    rows() { return this.receivables },
    isCustomerUser() {
      return this.currentUser?.role_name === 'customer'
    },
    currentCustomerId() {
      const scopedRoles = Array.isArray(this.currentUser?.scoped_roles) ? this.currentUser.scoped_roles : []
      const customerRole = scopedRoles.find(role => role.role_name === 'customer' && role.role_resource_type === 'Customer')
        || scopedRoles.find(role => role.role_resource_type === 'Customer')

      return customerRole?.role_resource_id ? String(customerRole.role_resource_id) : ''
    },
    customerOptions() {
      return [{ value: '', text: 'Todos' }].concat(
        (this.receivablesCustomers || []).map(c => ({ value: String(c.id), text: c.name })),
      )
    },
    projectOptions() {
      return [{ value: '', text: 'Todos' }].concat(
        (this.receivablesProjects || []).map(p => ({ value: String(p.id), text: p.name })),
      )
    },
    creditHeader() { return this.receivablesCreditHeader },
    isOverLimit() {
      return Boolean(this.creditHeader?.is_over_limit)
    },
    creditSummaryMetrics() {
      const summary = this.creditHeader?.summary || {}
      return [
        {
          key: 'total', label: 'Total', value: summary.total, class: 'is-total',
        },
        {
          key: 'current', label: 'Al corriente', value: summary.al_corriente, class: 'is-current',
        },
        { key: 'a_1_15', label: '1 a 15', value: summary.a_1_15 },
        { key: 'a_16_30', label: '16 a 30', value: summary.a_16_30 },
        { key: 'a_30_mas', label: '+30', value: summary.a_30_mas },
        {
          key: 'overdue', label: 'Vencido', value: summary.vencido, class: 'is-overdue',
        },
      ]
    },
    canIssueRep() {
      return !this.isCustomerUser && Boolean(this.filters.customer_id && this.creditHeader)
    },
    canSubmitRep() {
      return this.canIssueRep
        && !this.loading
        && !this.generatingRep
        && Boolean(this.selectedRepTaxInformationId)
        && Boolean(this.effectiveRepPaymentDate)
        && Boolean(this.selectedPendingRepId)
        && Number(this.effectiveRepAmount || 0) > 0
        && this.selectedRepRows.length > 0
        && !this.hasInvalidRepAmounts
        && this.repAmountMatchesSelectedTotal
    },
    canSaveRepReceipt() {
      const base = this.canIssueRep
        && !this.loading
        && !this.generatingRep
        && Boolean(this.selectedRepTaxInformationId)
        && Boolean(this.effectiveRepPaymentDate)
        && Number(this.effectiveRepAmount || 0) > 0

      if (!base) return false
      if (this.hasInvalidRepAmounts) return false
      if (!this.selectedPendingRepId) return true

      return this.selectedRepRows.length > 0 && this.normalizedRepAmount > this.normalizedSelectedRepTotal
    },
    selectedRepTotal() {
      return this.selectedRepRows.reduce((sum, row) => sum + Number(row.repAmount || 0), 0)
    },
    repUnassignedAmount() {
      const amount = Number(this.effectiveRepAmount || 0)
      if (amount <= 0) return 0

      return Math.max(0, Number((amount - this.selectedRepTotal).toFixed(2)))
    },
    hasInvalidRepAmounts() {
      return this.selectedRepRows.some(row => {
        const amount = Number(row.repAmount || 0)
        return amount <= 0 || amount > Number(row.repBalance || 0) + 0.005
      })
    },
    normalizedRepAmount() {
      return this.toCents(this.effectiveRepAmount)
    },
    normalizedSelectedRepTotal() {
      return this.toCents(this.selectedRepTotal)
    },
    repAssignmentProgress() {
      const amount = Number(this.effectiveRepAmount || 0)
      if (amount <= 0) return 0

      return Math.min(100, Math.round((Number(this.selectedRepTotal || 0) / amount) * 100))
    },
    repProgressVariant() {
      if (!this.effectiveRepAmount || !this.selectedRepRows.length) return 'secondary'
      if (this.normalizedRepAmount === this.normalizedSelectedRepTotal) return 'success'
      if (this.normalizedRepAmount > this.normalizedSelectedRepTotal) return 'warning'

      return 'info'
    },
    repAmountMatchesSelectedTotal() {
      return this.normalizedRepAmount === this.normalizedSelectedRepTotal
    },
    repAmountMessage() {
      if (!this.effectiveRepAmount) return ''
      if (this.hasInvalidRepAmounts) return 'Revisa el monto a aplicar de las facturas seleccionadas'
      if (this.selectedPendingRepId && !this.selectedRepRows.length) return 'Selecciona facturas PPD para relacionar este pago'
      if (!this.selectedRepRows.length) return ''

      const difference = this.normalizedRepAmount - this.normalizedSelectedRepTotal
      if (difference === 0) return 'Monto completamente asignado'
      if (difference > 0) return `Faltan $ ${this.money(difference / 100)} por asignar entre las facturas`

      return `Asignaste $ ${this.money(Math.abs(difference) / 100)} de más que el pago recibido; ajusta los montos para poder emitir`
    },
    repTaxInformationOptions() {
      const balances = this.creditHeader?.rep_credit_balances || []
      return [{ value: '', text: 'Razón social del REP' }].concat(
        balances.map(item => ({
          value: item.tax_information_id,
          text: `${item.corporate_name || 'Sin razón social'} · ${item.rfc || 'RFC'} · Pendiente $ ${this.money(item.available_amount)}`,
        })),
      )
    },
    selectedRepAvailableBalance() {
      const match = (this.creditHeader?.rep_credit_balances || [])
        .find(item => item.tax_information_id === this.selectedRepTaxInformationId)
      return Number(match?.available_amount || 0)
    },
    pendingRepHistory() {
      return (this.repHistory || []).filter(item => item.status !== 'emitted' && Number(item.remaining_amount || 0) > 0)
    },
    pendingRepCount() {
      return this.pendingRepHistory.length
    },
    emittedRepCount() {
      return (this.repHistory || []).filter(item => item.status === 'emitted').length
    },
    canceledRepCount() {
      return (this.repHistory || []).filter(item => item.status === 'canceled').length
    },
    selectedPendingRep() {
      return this.pendingRepHistory.find(item => item.id === this.selectedPendingRepId) || null
    },
    selectedPendingRepRows() {
      return this.selectedPendingRep ? [this.selectedPendingRep] : []
    },
    selectedPendingRepLabel() {
      if (!this.selectedPendingRep) return 'Ninguno'

      return `$ ${this.money(this.selectedPendingRep.remaining_amount)}`
    },
    effectiveRepAmount() {
      return this.selectedPendingRep ? Number(this.selectedPendingRep.remaining_amount || 0) : this.repAmount
    },
    effectiveRepPaymentDate() {
      return this.repPaymentDate
    },
    saveRepReceiptLabel() {
      if (this.generatingRep) return 'Guardando...'
      return this.selectedPendingRepId ? 'Guardar relación' : 'Guardar monto'
    },
    allowedReservedRepQuoteIds() {
      return this.pendingQuoteIdsFor(this.selectedPendingRepRows)
    },
    reservedRepQuoteIds() {
      const otherPendingRows = this.pendingRepHistory.filter(item => item.id !== this.selectedPendingRepId)
      return this.pendingQuoteIdsFor(otherPendingRows)
    },
    repHistoryStatusOptions() {
      return [
        { value: 'available', text: 'Pendientes' },
        { value: 'all', text: 'Todos' },
        { value: 'emitted', text: 'Emitidos' },
        { value: 'canceled', text: 'Cancelados' },
        { value: 'consumed', text: 'Consumidos' },
        { value: 'voided', text: 'Anulados' },
      ]
    },
    cancellationMotiveOptions() {
      return [
        { value: '02', text: '02 - Comprobante emitido con errores sin relación' },
        { value: '01', text: '01 - Errores con relación; requiere CFDI sustituto ya emitido' },
        { value: '03', text: '03 - No se llevó a cabo la operación' },
        { value: '04', text: '04 - Operación nominativa relacionada en factura global' },
      ]
    },
    canSubmitRepCancellation() {
      return Boolean(
        this.repToCancel
          && this.repCancellationForm.reason
          && this.repCancellationForm.motive
          && (this.repCancellationForm.motive !== '01' || this.repCancellationForm.substitution),
      )
    },
    canSubmitVoidPendingRep() {
      return Boolean(this.pendingRepToVoid && this.voidPendingRepForm.reason)
    },
    filteredRepHistory() {
      const query = this.normalizeSearch(this.repHistorySearch)

      return (this.repHistory || []).filter(item => {
        const matchesStatus = this.matchesRepHistoryStatus(item)
        if (!matchesStatus) return false
        if (!query) return true

        return this.normalizeSearch([
          item.facturapi_payment_invoice_reference,
          item.facturapi_payment_invoice_uuid,
          this.repHistoryStatusLabel(item),
          item.tax_information?.corporate_name,
          item.tax_information?.rfc,
          this.repHistoryQuoteSummary(item),
        ].filter(Boolean).join(' ')).includes(query)
      })
    },
    paginatedRepHistory() {
      const start = (this.repHistoryPage - 1) * this.repHistoryPerPage
      return this.filteredRepHistory.slice(start, start + this.repHistoryPerPage)
    },
    repHistoryFields() {
      return [
        { key: 'rep_select', label: '' },
        { key: 'created_at', label: 'Fecha' },
        { key: 'payment_date', label: 'Fecha pago' },
        { key: 'facturapi_payment_invoice_reference', label: 'REP' },
        { key: 'tax_information', label: 'Razón social' },
        { key: 'status', label: 'Estado' },
        { key: 'amount', label: 'Monto' },
        { key: 'applied_amount', label: 'Aplicado' },
        { key: 'allocations', label: 'Cotizaciones' },
        { key: 'facturapi_payment_invoice_uuid', label: 'UUID REP' },
        { key: 'actions', label: '' },
      ]
    },
  },

  watch: {
    // RUTA -> FILTROS (rehidrata cuando cambie la URL manualmente o con back/forward)
    '$route.query': {
      immediate: false,
      async handler(newQ) {
        if (this.suppressRouteSync) return
        const next = this.parseQuery(newQ || {})
        // Evita trabajo si no cambia nada
        const curr = this.serializeFilters()
        if (JSON.stringify(next) === JSON.stringify(curr)) return
        await this.hydrateAndFetch(next)
      },
    },
    // FILTROS -> RUTA (refleja cambios del usuario en la URL, sin bucles)
    filters: {
      deep: true,
      handler() {
        if (this.suppressRouteSync) return
        const q = this.serializeFilters()
        // Evitar replace innecesario
        if (JSON.stringify(q) !== JSON.stringify(this.$route.query || {})) {
          this.suppressRouteSync = true
          this.$router.replace({ query: q }).catch(() => {})
          // Liberar el candado al próximo tick
          this.$nextTick(() => { this.suppressRouteSync = false })
        }
      },
    },
    selectedRepTaxInformationId() {
      this.selectedRepRows = []
      this.selectedPendingRepId = ''
      this.repPaymentDate = this.defaultRepPaymentDate()
      if (this.$refs.receivablesTable?.clearRepSelection) {
        this.$refs.receivablesTable.clearRepSelection()
      }
      if (!this.repAmount && this.selectedRepAvailableBalance > 0) {
        this.repAmount = this.selectedRepAvailableBalance
      }
      this.fetchRepHistory()
    },
    repHistorySearch() {
      this.repHistoryPage = 1
    },
    repHistoryStatusFilter() {
      this.repHistoryPage = 1
    },
    filteredRepHistory() {
      const totalPages = Math.max(1, Math.ceil(this.filteredRepHistory.length / this.repHistoryPerPage))
      if (this.repHistoryPage > totalPages) {
        this.repHistoryPage = totalPages
      }
    },
  },

  created() {
    this.repPaymentDate = this.defaultRepPaymentDate()
    const fromQ = this.parseQuery(this.$route.query || {})
    this.hydrateAndFetch(fromQ)
  },

  methods: {
    ...mapActions('finance', {
      fetchReceivables: 'fetchReceivables',
      fetchSummary: 'fetchReceivablesSummary',
      fetchCustomers: 'fetchReceivablesCustomers',
      fetchProjects: 'fetchReceivablesProjects',
      fetchCreditHeader: 'fetchReceivablesCreditHeader',
      exportCsv: 'exportReceivablesCsv',
      markPaid: 'markPaid',
      payQuotePartial: 'payQuotePartial',
      unmarkPaid: 'unmarkPaid',
      setQuoteCheckplusReference: 'setQuoteCheckplusReference',
      clearQuoteCheckplusReference: 'clearQuoteCheckplusReference',
      issueReceivablesPaymentComplement: 'issueReceivablesPaymentComplement',
      fetchReceivablesPaymentComplements: 'fetchReceivablesPaymentComplements',
      cancelReceivablesPaymentComplement: 'cancelReceivablesPaymentComplement',
      syncReceivablesPaymentComplementCancellation: 'syncReceivablesPaymentComplementCancellation',
      voidReceivablesPendingPayment: 'voidReceivablesPendingPayment',
    }),

    money(v) {
      const n = Number(v || 0)
      return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    defaultRepPaymentDate() {
      const date = new Date()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${date.getFullYear()}-${month}-${day}`
    },
    formatDate(value) {
      if (!value) return '—'

      const parts = String(value).split('-')
      if (parts.length !== 3) return value

      return `${parts[2]}-${parts[1]}-${parts[0]}`
    },
    formatDateTime(value) {
      if (!value) return '—'

      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '—'

      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')

      return `${day}-${month}-${year} ${hours}:${minutes}`
    },
    toCents(value) {
      return Math.round(Number(value || 0) * 100)
    },
    normalizeSearch(value) {
      return String(value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
    },
    matchesRepHistoryStatus(item) {
      if (this.repHistoryStatusFilter === 'all') return true
      if (this.repHistoryStatusFilter === 'available') return this.isPendingRepSelectable(item)
      if (this.repHistoryStatusFilter === 'emitted') return item.status === 'emitted'
      if (this.repHistoryStatusFilter === 'canceled') return item.status === 'canceled'
      if (this.repHistoryStatusFilter === 'voided') return item.status === 'voided'
      if (this.repHistoryStatusFilter === 'consumed') {
        return ['consumed', 'partially_consumed'].includes(item.status)
      }

      return true
    },
    repHistoryQuoteSummary(item) {
      const rows = item.status === 'emitted' ? item.allocations : item.selected_quotes
      const list = Array.isArray(rows) ? rows : []
      if (!list.length && item.restored_from_canceled_rep_reference) {
        return `Pago recuperado de ${item.restored_from_canceled_rep_reference}`
      }
      if (!list.length) return '—'

      return list.map(row => {
        const amount = row.amount || row.balance || 0
        return `F${row.folio} ($ ${this.money(amount)})`
      }).join(', ')
    },
    repHistoryStatusLabel(item) {
      if (item.status === 'canceled') return 'Cancelado'
      if (item.status === 'emitted') return 'Emitido'
      if (item.status === 'consumed') return 'Consumido'
      if (item.status === 'partially_consumed') return 'Parcialmente consumido'
      if (item.status === 'voided') return 'Anulado'
      return 'Pendiente'
    },
    isPendingAllocationRep(item) {
      return item?.status === 'pending_allocation' || (!item?.facturapi_payment_invoice_id && !['canceled', 'consumed', 'partially_consumed', 'voided'].includes(item?.status))
    },
    pendingQuoteIdsFor(rows) {
      return Array.from(new Set((rows || []).flatMap(item => (
        Array.isArray(item.selected_quotes) ? item.selected_quotes : []
      ).map(quote => String(quote.quote_id || '')).filter(Boolean))))
    },
    isPendingRepSelectable(item) {
      return !['emitted', 'canceled'].includes(item?.status) && Number(item?.remaining_amount || 0) > 0
    },
    canCancelRep(item) {
      return this.currentUser?.role_name === 'admin'
        && item?.status === 'emitted'
        && item?.facturapi_payment_invoice_id
        && item?.facturapi_payment_invoice_status !== 'canceled'
        && item?.facturapi_payment_invoice_cancellation_status !== 'pending'
    },
    canSyncRepCancellation(item) {
      return this.currentUser?.role_name === 'admin'
        && item?.facturapi_payment_invoice_id
        && item?.facturapi_payment_invoice_cancellation_status === 'pending'
    },
    canVoidPendingRep(item) {
      return this.currentUser?.role_name === 'admin'
        && item?.status !== 'voided'
        && !item?.facturapi_payment_invoice_id
        && Number(item?.remaining_amount || 0) > 0
    },
    onPendingRepSelected(item) {
      this.selectedPendingRepId = item.id
      this.repPaymentDate = item.payment_date || this.defaultRepPaymentDate()
      const selectedPendingQuoteIds = this.allowedReservedRepQuoteIds
      const currentExtraRows = this.selectedRepRows
        .filter(row => !this.reservedRepQuoteIds.includes(String(row.quoteId)))
      const quoteIds = Array.from(new Set(
        currentExtraRows.map(row => String(row.quoteId)).concat(selectedPendingQuoteIds),
      ))
      const amounts = {}
      currentExtraRows.forEach(row => {
        amounts[String(row.quoteId)] = row.repAmount
      })
      const pendingQuotes = Array.isArray(item.selected_quotes) ? item.selected_quotes : []
      let unassigned = Number(item.remaining_amount || 0)
      pendingQuotes.forEach(quote => {
        const quoteId = String(quote.quote_id || '')
        if (!quoteId) return

        const amount = quote.amount === null || quote.amount === undefined ? quote.balance : quote.amount
        if (amount === null || amount === undefined) return

        const capped = Math.min(Number(amount) || 0, Math.max(0, unassigned))
        amounts[quoteId] = capped
        unassigned = Number((unassigned - capped).toFixed(2))
      })

      if (this.$refs.receivablesTable?.setRepSelection) {
        this.$refs.receivablesTable.setRepSelection(quoteIds, amounts)
      }
      this.repAmount = Number(item.remaining_amount || 0) || null
    },
    clearPendingRepSelection() {
      this.selectedPendingRepId = ''
      this.selectedRepRows = []
      this.repAmount = null
      this.repPaymentDate = this.defaultRepPaymentDate()
      if (this.$refs.receivablesTable?.clearRepSelection) {
        this.$refs.receivablesTable.clearRepSelection()
      }
    },

    async onCustomerChange() {
      this.loading = true
      try {
        const customerId = this.effectiveCustomerId(this.filters.customer_id)
        await this.fetchProjects({ customer_id: customerId })
        if (this.filters.customer_id !== '') {
          await this.fetchCreditHeader({ customer_id: customerId })
          await this.fetchSummary({ customer_id: customerId })
          this.selectedRepTaxInformationId = this.creditHeader?.rep_credit_balances?.[0]?.tax_information_id || ''
          this.repAmount = this.selectedRepAvailableBalance || null
          await this.fetchRepHistory()
        }
        this.filters.project_id = ''
        this.filters.customer_id = customerId
        await this.fetchReceivables({ ...this.filters, customer_id: customerId })
      } finally { this.loading = false }
    },

    async refetch() {
      this.loading = true
      try {
        const customerId = this.effectiveCustomerId(this.filters.customer_id)
        if (customerId !== '') {
          await this.fetchSummary({ customer_id: customerId })
          await this.fetchCreditHeader({ customer_id: customerId })
          await this.fetchRepHistory()
        }
        await this.fetchReceivables({ ...this.filters, customer_id: customerId })
      } finally { this.loading = false }
    },

    applySearch() {
      this.filters.q = (this.searchText || '').trim()
      this.refetch()
    },

    async onMarkPaid(quoteId) {
      this.loading = true
      try {
        await this.markPaid({ quoteId })
        await this.refetch()
        const customerId = this.effectiveCustomerId(this.filters.customer_id)
        if (customerId !== '') {
          await this.fetchCreditHeader({ customer_id: customerId })
        }
      } finally { this.loading = false }
    },
    async onPayPartial({ quoteId, amount }) {
      this.loading = true
      try {
        await this.payQuotePartial({ quoteId, amount })
        await this.refetch()
        const customerId = this.effectiveCustomerId(this.filters.customer_id)
        if (customerId !== '') {
          await this.fetchCreditHeader({ customer_id: customerId })
        }
      } finally { this.loading = false }
    },

    async onUnmarkPaid(quoteId) {
      this.loading = true
      try {
        await this.unmarkPaid({ quoteId })
        await this.refetch()
        const customerId = this.effectiveCustomerId(this.filters.customer_id)
        if (customerId !== '') {
          await this.fetchCreditHeader({ customer_id: customerId })
        }
      } finally { this.loading = false }
    },

    async onIssueRep() {
      if (!this.canSubmitRep) return

      await this.submitRep({ saveUnallocated: false })
    },
    async onSaveRepReceipt() {
      if (!this.canSaveRepReceipt) return

      await this.submitRep({ saveUnallocated: true, saveOnlyReceipt: !this.selectedPendingRepId || this.selectedRepRows.length === 0 })
    },
    async submitRep({ saveUnallocated, saveOnlyReceipt = false }) {
      this.generatingRep = true
      try {
        const customerId = this.effectiveCustomerId(this.filters.customer_id)
        const response = await this.issueReceivablesPaymentComplement({
          customerId,
          projectId: this.filters.project_id || null,
          taxInformationId: this.selectedRepTaxInformationId,
          quoteIds: saveOnlyReceipt ? [] : this.selectedRepRows.map(row => row.quoteId),
          quoteAmounts: saveOnlyReceipt
            ? []
            : this.selectedRepRows.map(row => ({ quote_id: row.quoteId, amount: row.repAmount })),
          pendingRepIds: saveOnlyReceipt ? [] : [this.selectedPendingRepId],
          amount: this.effectiveRepAmount,
          paymentDate: this.effectiveRepPaymentDate,
          saveUnallocated,
        })
        const appliedCount = Array.isArray(response?.applied_quotes) ? response.applied_quotes.length : 0
        let title = 'Monto recibido guardado sin facturas relacionadas.'
        if (response?.emitted) {
          title = `REP generado. Cotizaciones liquidadas: ${appliedCount}.`
        } else if (!saveOnlyReceipt) {
          title = `Relación guardada. ${this.repAmountMessage || 'Pendiente de asignación completa'}.`
        }

        this.$toast({
          component: ToastificationContent,
          props: {
            title,
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        })
        this.repAmount = null
        this.repPaymentDate = this.defaultRepPaymentDate()
        this.selectedRepRows = []
        this.selectedPendingRepId = ''
        if (this.$refs.receivablesTable?.clearRepSelection) {
          this.$refs.receivablesTable.clearRepSelection()
        }
        await this.refetch()
        await this.fetchCreditHeader({ customer_id: customerId })
        await this.fetchRepHistory()
      } catch (error) {
        const messages = await this.errorMessages(error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: messages[0] || 'No se pudo generar el REP',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.generatingRep = false
      }
    },
    onRepSelectionChange(rows) {
      this.selectedRepRows = Array.isArray(rows) ? rows : []
      if (!this.selectedPendingRepId && !this.repAmount && this.selectedRepTotal > 0) {
        this.repAmount = this.selectedRepTotal
      }
    },
    async fetchRepHistory() {
      const customerId = this.effectiveCustomerId(this.filters.customer_id)
      if (!customerId) {
        this.repHistory = []
        return
      }

      const response = await this.fetchReceivablesPaymentComplements({
        customerId,
        taxInformationId: this.selectedRepTaxInformationId || null,
      })
      this.repHistory = response?.data || []
      if (this.selectedPendingRepId && !this.pendingRepHistory.some(item => item.id === this.selectedPendingRepId)) {
        this.selectedPendingRepId = ''
        this.repPaymentDate = this.defaultRepPaymentDate()
      }
    },
    openCancelRepModal(item) {
      this.repToCancel = item
      this.$bvModal.show('cancelReceivableRep')
    },
    resetRepCancellation() {
      this.repToCancel = null
      this.repCancellationForm = {
        motive: '02',
        substitution: '',
        reason: '',
      }
    },
    openVoidPendingRepModal(item) {
      this.pendingRepToVoid = item
      this.$bvModal.show('voidPendingReceivablePayment')
    },
    resetVoidPendingRep() {
      this.pendingRepToVoid = null
      this.voidPendingRepForm = {
        reason: '',
      }
    },
    async cancelSelectedRep() {
      if (!this.canSubmitRepCancellation) return

      this.cancelingRep = true
      try {
        const response = await this.cancelReceivablesPaymentComplement({
          repId: this.repToCancel.id,
          motive: this.repCancellationForm.motive,
          substitution: this.repCancellationForm.substitution,
          reason: this.repCancellationForm.reason,
        })
        this.$toast({
          component: ToastificationContent,
          props: {
            title: response?.message || 'REP cancelado correctamente',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        })
        this.$bvModal.hide('cancelReceivableRep')
        await this.refetch()
      } catch (error) {
        const messages = await this.errorMessages(error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: messages[0] || 'No se pudo cancelar el REP',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.cancelingRep = false
      }
    },
    async voidSelectedPendingRep() {
      if (!this.canSubmitVoidPendingRep) return

      this.voidingPendingRep = true
      try {
        const response = await this.voidReceivablesPendingPayment({
          repId: this.pendingRepToVoid.id,
          reason: this.voidPendingRepForm.reason,
        })
        this.$toast({
          component: ToastificationContent,
          props: {
            title: response?.message || 'Pago guardado anulado correctamente',
            icon: 'CheckCircleIcon',
            variant: 'success',
          },
        })
        if (this.selectedPendingRepId === this.pendingRepToVoid.id) {
          this.clearPendingRepSelection()
        }
        this.$bvModal.hide('voidPendingReceivablePayment')
        await this.refetch()
      } catch (error) {
        const messages = await this.errorMessages(error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: messages[0] || 'No se pudo anular el pago guardado',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.voidingPendingRep = false
      }
    },
    async syncRepCancellation(item) {
      if (!this.canSyncRepCancellation(item)) return

      this.syncingRepCancellation = true
      try {
        const response = await this.syncReceivablesPaymentComplementCancellation({
          repId: item.id,
        })
        this.$toast({
          component: ToastificationContent,
          props: {
            title: response?.message || 'Estado de cancelación actualizado',
            icon: 'RefreshCwIcon',
            variant: response?.cancellation?.state === 'canceled' ? 'success' : 'warning',
          },
        })
        await this.refetch()
      } catch (error) {
        const messages = await this.errorMessages(error)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: messages[0] || 'No se pudo actualizar la cancelación',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.syncingRepCancellation = false
      }
    },

    async onExportCsv() {
      this.loading = true
      try {
        const res = await this.exportCsv({ ...this.filters })
        const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `estado_cuenta_${new Date().toISOString().slice(0, 10)}.csv`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      } finally { this.loading = false }
    },

    async onSaveInvoice({ quoteId, checkplusReference, done }) {
      await this.setQuoteCheckplusReference({ quoteId, checkplusReference })
      await this.refetch()
      const customerId = this.effectiveCustomerId(this.filters.customer_id)
      if (customerId !== '') {
        await this.fetchCreditHeader({ customer_id: customerId })
      }
      if (typeof done === 'function') done()
    },

    onPrint() {
      this.$nextTick(() => window.print())
    },
    async errorMessages(error) {
      if (error?.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text()
          const payload = JSON.parse(text)
          return payload.messages || []
        } catch (e) {
          return []
        }
      }

      return error?.data?.messages || error?.response?.data?.messages || []
    },
    // -------- Sincronización robusta --------
    async hydrateAndFetch(next) {
      // Evita que el watcher de filtros empuje a la URL mientras hidratamos
      this.suppressRouteSync = true
      try {
        // 1) Cargar catálogos primero
        if (!this.receivablesCustomers || this.receivablesCustomers.length === 0) {
          await this.fetchCustomers()
        }
        // 2) Aplicar filtros venidos de la URL
        this.filters = { ...this.filters, ...next }
        if (this.isCustomerUser && this.currentCustomerId) {
          this.filters.customer_id = this.currentCustomerId
        }
        this.searchText = this.filters.q || ''

        // 3) Si hay cliente, cargar proyectos y headers antes de pedir la lista
        const customerId = this.effectiveCustomerId(this.filters.customer_id)
        this.filters.customer_id = customerId
        if (customerId !== '') {
          await this.fetchProjects({ customer_id: customerId })
          await this.fetchCreditHeader({ customer_id: customerId })
          await this.fetchSummary({ customer_id: customerId })
          if (!this.selectedRepTaxInformationId && this.creditHeader?.rep_credit_balances?.length) {
            this.selectedRepTaxInformationId = this.creditHeader.rep_credit_balances[0].tax_information_id
          }
          this.repAmount = this.selectedRepAvailableBalance || null
          await this.fetchRepHistory()
        }
        // 4) Traer lista
        await this.fetchReceivables({ ...this.filters, customer_id: customerId })

        // 5) Asegurar que la URL refleje lo actual (por si faltaba algo)
        const q = this.serializeFilters()
        if (JSON.stringify(q) !== JSON.stringify(this.$route.query || {})) {
          await this.$router.replace({ query: q }).catch(() => {})
        }
      } finally {
        // liberar candado
        this.suppressRouteSync = false
      }
    },

    // FILTROS -> query (guardamos booleanos como 1/0)
    serializeFilters() {
      const q = {}
      if (this.filters.customer_id && !this.isCustomerUser) q.customer_id = this.filters.customer_id
      if (this.filters.project_id) q.project_id = this.filters.project_id
      if (this.filters.q) q.q = this.filters.q
      q.unpaid_only = this.filters.unpaid_only ? '1' : '0'
      q.only_overdue = this.filters.only_overdue ? '1' : '0'
      if (this.filters.pending_complement_only && !this.isCustomerUser) q.pending_complement_only = '1'
      return q
    },
    // query -> FILTROS (tipos correctos)
    parseQuery(q) {
      const toBool = (v, def) => (v === undefined ? def : v === '1' || v === 'true' || v === true)
      const customerId = q && q.customer_id ? String(q.customer_id) : ''
      return {
        customer_id: this.isCustomerUser ? this.currentCustomerId : customerId,
        project_id: q && q.project_id ? String(q.project_id) : '',
        unpaid_only: toBool(q.unpaid_only, true),
        only_overdue: toBool(q.only_overdue, false),
        pending_complement_only: this.isCustomerUser ? false : toBool(q.pending_complement_only, false),
        q: q && q.q ? String(q.q) : '',
      }
    },
    effectiveCustomerId(value) {
      if (this.isCustomerUser) return this.currentCustomerId || ''
      return value || ''
    },
  },
}
</script>

<style>
/* Ocultar/mostrar en impresión */
.no-print { }
.print-only { display: none; }

.yc-credit-card.is-over-limit {
  border-color: rgba(234, 84, 85, 0.25);
  box-shadow: 0 12px 28px rgba(234, 84, 85, 0.08);
}

.yc-credit-card {
  border: 1px solid #ebe9f1;
  border-radius: 10px;
  box-shadow: 0 12px 28px rgba(67, 51, 92, 0.06);
}

.yc-credit-header {
  display: flex;
  justify-content: space-between;
  gap: 24px;
}

.yc-client-block {
  min-width: 0;
}

.yc-eyebrow {
  margin-bottom: 4px;
  color: #8a86a3;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
}

.yc-client-title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.yc-client-title {
  margin: 0;
  color: #4c4667;
  font-weight: 700;
}

.yc-status-badge {
  font-size: 0.72rem;
}

.yc-client-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-top: 4px;
  color: #8a86a3;
  font-size: 0.86rem;
}

.yc-credit-limits {
  display: grid;
  grid-template-columns: repeat(2, minmax(140px, 1fr));
  gap: 8px;
  min-width: 300px;
}

.yc-limit-item {
  padding: 10px 12px;
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  background: #fbfafd;
}

.yc-limit-item span,
.yc-aging-metric small {
  display: block;
  color: #8a86a3;
  font-size: 0.76rem;
}

.yc-limit-item strong,
.yc-aging-metric strong {
  display: block;
  margin-top: 2px;
  color: #4c4667;
  font-size: 1rem;
  white-space: nowrap;
}

.yc-limit-item.is-negative strong,
.yc-aging-metric.is-overdue strong {
  color: #ea5455;
}

.yc-credit-subtitle {
  margin-top: 4px;
  color: #8a86a3;
  font-size: 0.85rem;
}

.yc-credit-alert {
  margin-top: 12px;
  padding: 10px 14px;
  border-radius: 12px;
  background: #fff2f2;
  border: 1px solid #ffd7d7;
  color: #d64545;
  font-weight: 600;
}

.yc-rep-panel {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #ebe9f1;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(380px, auto);
  gap: 16px;
  align-items: end;
}

.yc-rep-info {
  min-width: 0;
}

.yc-rep-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.yc-rep-title {
  margin: 0;
  color: #4c4667;
  font-weight: 700;
}

.yc-rep-flow {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.yc-rep-step {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 9px;
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  color: #6e6b7b;
  font-size: 0.78rem;
  font-weight: 600;
}

.yc-rep-step span {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #f0edf6;
  color: #5e2b97;
  font-size: 0.7rem;
  font-weight: 700;
}

.yc-rep-progress {
  margin-top: 12px;
  max-width: 620px;
}

.yc-rep-progress-labels {
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
  color: #6e6b7b;
  font-size: 0.78rem;
  font-weight: 600;
}

.yc-rep-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.yc-rep-input {
  width: 180px;
}

.yc-rep-date-input {
  width: 160px;
}

.yc-rep-tax-select {
  width: 420px;
  max-width: 100%;
}

.yc-rep-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  margin-top: 8px;
  color: #6e6b7b;
  font-size: 0.8rem;
  font-weight: 600;
}

.yc-rep-new-payment {
  white-space: nowrap;
}

.yc-aging-summary {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 8px;
  margin-top: 16px;
}

.yc-aging-metric {
  padding: 10px 12px;
  border: 1px solid #ebe9f1;
  border-radius: 8px;
  background: #fff;
}

.yc-aging-metric.is-total {
  background: #f7f5fb;
}

.yc-aging-metric.is-current strong {
  color: #28c76f;
}

.yc-rep-history {
  margin-top: 8px;
  padding: 10px 12px 0;
  border: 1px solid #ebe9f1;
  border-radius: 10px;
  background: #fff;
}

.yc-rep-history-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 8px;
}

.yc-rep-history-header h5 {
  margin: 0;
  color: #4c4667;
  font-weight: 700;
}

.yc-rep-history-header small,
.yc-rep-history-footer small {
  color: #8a86a3;
}

.yc-rep-history-tools {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}

.yc-rep-history-search {
  width: 220px;
}

.yc-rep-history-filter {
  width: 150px;
}

.yc-rep-history-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0 10px;
  border-top: 1px solid #f0edf6;
}

@media (max-width: 1199px) {
  .yc-rep-panel {
    grid-template-columns: 1fr;
  }

  .yc-rep-actions {
    justify-content: flex-start;
  }
}

@media (max-width: 991px) {
  .yc-credit-header,
  .yc-rep-history-header,
  .yc-rep-history-footer {
    flex-direction: column;
    align-items: stretch;
  }

  .yc-credit-limits,
  .yc-aging-summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    min-width: 0;
  }

  .yc-rep-history-tools,
  .yc-rep-actions {
    justify-content: stretch;
  }

  .yc-rep-history-search,
  .yc-rep-history-filter,
  .yc-rep-input,
  .yc-rep-date-input {
    width: 100%;
  }
}

@media (max-width: 575px) {
  .yc-credit-limits,
  .yc-aging-summary {
    grid-template-columns: 1fr;
  }
}

@media print {
  .no-print { display: none !important; }
  .print-only { display: block !important; }
  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }

  .yc-print-header { border-bottom: 2px solid #000; padding-bottom: 6px; margin-bottom: 8px; }
  .yc-print-logo { height: 22px; margin-right: 8px; }
  .yc-company { font-weight: 700; }
  .yc-title { font-size: 12px; }
  .yc-money { white-space: nowrap; }
  table.table td.text-right,
  table.table th.text-right {
    white-space: nowrap;
  }

  .yc-print-note {
    margin-top: 10px;
    font-size: 11px;
    color: #444;
  }

  /* Grids para la cabecera y el resumen */
  .yc-print-grid-4,
  .yc-print-grid-6 {
    display: grid;
    grid-gap: 6px 12px;
    font-size: 12px;
  }

  .yc-print-grid-4 {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .yc-print-grid-6 {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }

  table.table { font-size: 11px; border-collapse: collapse !important; width: 100%; }
  table.table thead th { border: 1px solid #000 !important; background: #f2f2f2 !important; }
  table.table td { border: 1px solid #000 !important; }

  .table .btn, .table [role="button"], .table a.btn, .table .btn-link { display: none !important; }

  .b-overlay, .b-overlay-wrap { position: static !important; }
}

@media print {
  /* Menús superiores / laterales / inferiores comunes */
  nav, footer,
  .navbar, .navbar-fixed, .b-navbar,
  .sidebar, .b-sidebar, .v-navigation-drawer, .menu, .main-menu,
  .app-footer, .layout-footer, .footer, .app-bottom-nav, .bottom-nav {
    display: none !important;
    visibility: hidden !important;
    height: 0 !important;
    overflow: hidden !important;
  }
}
</style>
